import React, { useState, useCallback, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { Point, Area } from 'react-easy-crop/types';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { 
  Upload, 
  X, 
  Loader2, 
  AlertCircle,
  Check,
  Info
} from 'lucide-react';
import { profileImageServiceV2, type PhotoPersistMode } from '@/services/profileImageServiceV2';
import { toast } from 'sonner';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('DualPhotoUpload', 'user-management');

interface DualPhotoUploadProps {
  userId: string;
  facialPhoto?: string | null;
  bodyPhoto?: string | null;
  onFacialChange?: (url: string | null) => void;
  onBodyChange?: (url: string | null) => void;
  disabled?: boolean;
  showMigrationPrompt?: boolean;
  /** Partner-delegated editing persists through the audited partner RPC. */
  persistVia?: PhotoPersistMode;
}

interface CropModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  photoType: 'facial' | 'body';
  onCropComplete: (croppedAreaPixels: Area) => void;
  isProcessing: boolean;
}

const CropModal: React.FC<CropModalProps> = ({
  isOpen,
  onClose,
  imageUrl,
  photoType,
  onCropComplete,
  isProcessing
}) => {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const aspectRatio = photoType === 'facial' ? 1 : 2/3; // 1:1 for facial, 2:3 for body

  const onCropChange = useCallback((location: Point) => {
    setCrop(location);
  }, []);

  const onZoomChange = useCallback((zoom: number) => {
    setZoom(zoom);
  }, []);

  const onCropCompleteCallback = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleSave = () => {
    if (croppedAreaPixels) {
      onCropComplete(croppedAreaPixels);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            Crop {photoType === 'facial' ? 'Facial' : 'Body'} Photo
          </DialogTitle>
          <DialogDescription>
            Adjust the crop area to focus on the most important part of your photo.
            {photoType === 'facial' 
              ? ' For best results, center your face in the square.'
              : ' For best results, ensure your full body is visible in the frame.'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="relative h-96 bg-black rounded-lg overflow-hidden">
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            aspect={aspectRatio}
            onCropChange={onCropChange}
            onZoomChange={onZoomChange}
            onCropComplete={onCropCompleteCallback}
          />
        </div>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="zoom">Zoom</Label>
            <input
              id="zoom"
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="w-full"
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isProcessing || !croppedAreaPixels}
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              'Save & Upload'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const DualPhotoUpload: React.FC<DualPhotoUploadProps> = ({
  userId,
  facialPhoto,
  bodyPhoto,
  onFacialChange,
  onBodyChange,
  disabled = false,
  showMigrationPrompt = false,
  persistVia = 'direct'
}) => {
  const facialInputRef = useRef<HTMLInputElement>(null);
  const bodyInputRef = useRef<HTMLInputElement>(null);
  
  const [isUploading, setIsUploading] = useState<'facial' | 'body' | null>(null);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [uploadProgress, setUploadProgress] = useState<string>('');
  const [successType, setSuccessType] = useState<'facial' | 'body' | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [currentCropImage, setCurrentCropImage] = useState<string>('');
  const [currentCropType, setCurrentCropType] = useState<'facial' | 'body'>('facial');
  const [currentFile, setCurrentFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleFileSelect = (
    e: React.ChangeEvent<HTMLInputElement>,
    photoType: 'facial' | 'body'
  ) => {
    const file = e.target.files?.[0];
    if (!file || disabled) {
return;
}

    // Create preview URL for cropping
    const previewUrl = URL.createObjectURL(file);
    setCurrentFile(file);
    setCurrentCropImage(previewUrl);
    setCurrentCropType(photoType);
    setCropModalOpen(true);
  };

  const handleCropComplete = async (croppedAreaPixels: Area) => {
    if (!currentFile) {
return;
}
    
    setIsProcessing(true);
    setIsUploading(currentCropType);
    setUploadStatus('uploading');
    setUploadProgress('Processing image...');
    setErrorMessage('');
    
    try {
      log.info('Uploading photo with crop', { 
        userId, 
        photoType: currentCropType,
        cropData: croppedAreaPixels 
      });
      
      setUploadProgress('Uploading to server...');
      
      const result = await profileImageServiceV2.uploadDualPhoto(
        userId,
        currentFile,
        currentCropType,
        {
          x: croppedAreaPixels.x,
          y: croppedAreaPixels.y,
          width: croppedAreaPixels.width,
          height: croppedAreaPixels.height,
          scale: 1
        },
        { persistVia }
      );
      
      setUploadProgress('Saving changes...');
      
      // Update the appropriate photo
      if (currentCropType === 'facial') {
        onFacialChange?.(result.url);
      } else {
        onBodyChange?.(result.url);
      }
      
      // Show success state
      setUploadStatus('success');
      setSuccessType(currentCropType);
      toast.success(`${currentCropType === 'facial' ? 'Facial' : 'Body'} photo uploaded successfully`);
      log.info('Photo upload successful', { 
        userId, 
        photoType: currentCropType, 
        url: result.url 
      });
      
      // Close modal and clean up after a brief delay to show success
      setTimeout(() => {
        setCropModalOpen(false);
        URL.revokeObjectURL(currentCropImage);
        setCurrentFile(null);
        setCurrentCropImage('');
        // Reset success state after modal closes
        setTimeout(() => {
          setUploadStatus('idle');
          setSuccessType(null);
        }, 500);
      }, 1500);
    } catch (error) {
      log.error('Photo upload failed', { 
        userId, 
        photoType: currentCropType 
      }, error as Error);
      
      const errorMsg = error instanceof Error ? error.message : 'Failed to upload photo';
      setErrorMessage(errorMsg);
      setUploadStatus('error');
      toast.error(errorMsg);
    } finally {
      setIsProcessing(false);
      setIsUploading(null);
      setUploadProgress('');
    }
  };

  const handleRemove = async (photoType: 'facial' | 'body') => {
    if (disabled || isUploading) {
return;
}
    
    setIsUploading(photoType);
    try {
      log.info('Deleting photo', { userId, photoType });
      await profileImageServiceV2.deleteProfileImage(userId, photoType, { persistVia });
      
      if (photoType === 'facial') {
        onFacialChange?.(null);
      } else {
        onBodyChange?.(null);
      }
      
      toast.success('Photo removed successfully');
    } catch (error) {
      log.error('Photo deletion failed', { userId, photoType }, error as Error);
      toast.error('Failed to remove photo');
    } finally {
      setIsUploading(null);
    }
  };

  const PhotoCard = ({ 
    type, 
    photo, 
    inputRef 
  }: { 
    type: 'facial' | 'body';
    photo: string | null | undefined;
    inputRef: React.RefObject<HTMLInputElement>;
  }) => {
    const isFacial = type === 'facial';
    const title = isFacial ? 'Facial Photo' : 'Body Photo';
    const description = isFacial 
      ? 'Professional headshot for your profile'
      : 'Full body professional photo';
    
    return (
      <Card
        className={cn(
          'dual-photo-upload__card flex-1',
          `dual-photo-upload__card--${type}`,
          photo ? 'dual-photo-upload__card--populated' : 'dual-photo-upload__card--empty'
        )}
        data-photo-type={type}
        data-photo-state={photo ? 'populated' : 'empty'}
      >
        <CardHeader className="dual-photo-upload__header">
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="dual-photo-upload__content space-y-4">
          <div className="dual-photo-upload__preview relative">
            <div className={cn(
              "dual-photo-upload__preview-frame",
              "border-2 border-dashed border-border rounded-lg flex items-center justify-center bg-muted/50 overflow-hidden",
              isFacial ? "aspect-square" : "aspect-[2/3]",
              "min-h-[200px]"
            )}>
              {isUploading === type ? (
                <div className="flex flex-col items-center justify-center gap-3">
                  {uploadStatus === 'success' && successType === type ? (
                    <>
                      <div className="rounded-full bg-green-100 p-3">
                        <Check className="w-8 h-8 text-green-600" />
                      </div>
                      <p className="text-sm font-medium text-green-600">Upload successful!</p>
                    </>
                  ) : uploadStatus === 'error' && isUploading === type ? (
                    <>
                      <div className="rounded-full bg-red-100 p-3">
                        <AlertCircle className="w-8 h-8 text-red-600" />
                      </div>
                      <p className="text-sm font-medium text-red-600">Upload failed</p>
                      <p className="text-xs text-muted-foreground text-center px-4">{errorMessage}</p>
                    </>
                  ) : (
                    <>
                      <Loader2 className="w-8 h-8 text-primary animate-spin" />
                      <p className="text-sm text-muted-foreground">{uploadProgress || 'Uploading...'}</p>
                    </>
                  )}
                </div>
              ) : photo ? (
                <img 
                  src={photo} 
                  alt={title} 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="text-center p-4">
                  <Upload className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">
                    {isFacial ? 'Upload headshot' : 'Upload full body photo'}
                  </p>
                </div>
              )}
            </div>
            
          </div>

          <div className="dual-photo-upload__actions">
            <Button
              type="button"
              variant={uploadStatus === 'error' && isUploading === type ? 'destructive' : 'outline'}
              onClick={() => {
                if (uploadStatus === 'error' && isUploading === type) {
                  // Reset error state and allow retry
                  setUploadStatus('idle');
                  setErrorMessage('');
                  setIsUploading(null);
                }
                inputRef.current?.click();
              }}
              disabled={disabled || (isUploading === type && uploadStatus !== 'error')}
            >
              {isUploading === type ? (
                uploadStatus === 'error' ? (
                  <>
                    <AlertCircle className="w-4 h-4 mr-2" />
                    Try Again
                  </>
                ) : uploadStatus === 'success' ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Upload Complete
                  </>
                ) : (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                )
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  {photo ? 'Replace' : 'Upload Photo'}
                </>
              )}
            </Button>
            {photo && !disabled && isUploading !== type && (
              <Button
                type="button"
                variant="outline"
                className="dual-photo-upload__remove"
                aria-label={`Remove ${title.toLowerCase()}`}
                onClick={() => handleRemove(type)}
              >
                <X className="w-4 h-4" aria-hidden="true" />
                Remove
              </Button>
            )}
          </div>
          
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFileSelect(e, type)}
            disabled={disabled || isUploading === type}
          />
          
          <p className="text-xs text-muted-foreground text-center">
            Max 10MB, JPEG/PNG/WebP
            <br />
            {isFacial ? 'Recommended: 600x600px' : 'Recommended: 600x900px'}
          </p>
        </CardContent>
      </Card>
    );
  };

  return (
    <>
      <div className="dual-photo-upload space-y-4">
        {showMigrationPrompt && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>Enhanced Profile Photos Available!</AlertTitle>
            <AlertDescription>
              You can now upload separate facial and body photos for a more professional presentation.
              Your existing photo has been preserved as your facial photo.
            </AlertDescription>
          </Alert>
        )}
        
        <div className="dual-photo-upload__grid flex gap-4 flex-col md:flex-row">
          <PhotoCard 
            type="facial" 
            photo={facialPhoto} 
            inputRef={facialInputRef}
          />
          <PhotoCard 
            type="body" 
            photo={bodyPhoto} 
            inputRef={bodyInputRef}
          />
        </div>
        
        {(facialPhoto && bodyPhoto) && (
          <Alert className="border-green-200 bg-green-50">
            <Check className="h-4 w-4 text-green-600" />
            <p className="mb-1 font-medium leading-none tracking-tight text-green-800">
              Profile Photos Complete
            </p>
            <AlertDescription className="text-green-700">
              Both facial and body photos have been uploaded. Your profile looks great!
            </AlertDescription>
          </Alert>
        )}
      </div>
      
      <CropModal
        isOpen={cropModalOpen}
        onClose={() => {
          setCropModalOpen(false);
          URL.revokeObjectURL(currentCropImage);
          setCurrentFile(null);
          setCurrentCropImage('');
        }}
        imageUrl={currentCropImage}
        photoType={currentCropType}
        onCropComplete={handleCropComplete}
        isProcessing={isProcessing}
      />
    </>
  );
};
